import { useEffect, useState } from 'react'
import { useAppStore } from '../store/appStore'
import { formatTime, getSunTimes, sunPosition } from '../lib/sun'
import { lightRelation, lightRelationLabel } from '../lib/frame'

type GeoHit = {
  name: string
  lat: number
  lng: number
  type?: string
  kind?: string
  city?: string
}

export function LocationPanel() {
  const pin = useAppStore((s) => s.pin)
  const dateTime = useAppStore((s) => s.dateTime)
  const illuminated = useAppStore((s) => s.illuminated)
  const buildingsLoading = useAppStore((s) => s.buildingsLoading)
  const mapStyle = useAppStore((s) => s.mapStyle)
  const frame = useAppStore((s) => s.frame)
  const savedSpots = useAppStore((s) => s.savedSpots)
  const setPin = useAppStore((s) => s.setPin)
  const setMapStyle = useAppStore((s) => s.setMapStyle)
  const setFrame = useAppStore((s) => s.setFrame)
  const addSavedSpot = useAppStore((s) => s.addSavedSpot)
  const removeSavedSpot = useAppStore((s) => s.removeSavedSpot)
  const pitchEnabled = useAppStore((s) => s.pitchEnabled)
  const setPitchEnabled = useAppStore((s) => s.setPitchEnabled)

  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<GeoHit[]>([])
  const [searching, setSearching] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    if (query.trim().length < 2) {
      setHits([])
      return
    }
    const t = window.setTimeout(async () => {
      setSearching(true)
      try {
        const params = new URLSearchParams({ q: query.trim() })
        if (pin) {
          params.set('lat', String(pin.lat))
          params.set('lng', String(pin.lng))
        }
        const res = await fetch(`/api/geocode?${params}`)
        if (res.ok) setHits(await res.json())
        else setHits([])
      } catch {
        setHits([])
      } finally {
        setSearching(false)
      }
    }, 280)
    return () => clearTimeout(t)
  }, [query, pin])

  const lat = pin?.lat ?? 51.1079
  const lng = pin?.lng ?? 17.0385
  const sun = sunPosition(dateTime, lat, lng)
  const times = getSunTimes(lat, lng, dateTime)
  const relation = frame?.active
    ? lightRelation(frame.bearing, sun.azimuthDeg)
    : null

  return (
    <section className="panel-glass rounded-2xl p-4 w-full max-w-sm space-y-3">
      <div>
        <p
          className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent-2)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Lokalizacja
        </p>
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
          Raport świetlny
        </h2>
      </div>

      <div className="relative">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ulica, adres, plac… np. Krakowskie Przedmieście"
          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm"
          autoComplete="off"
          spellCheck={false}
        />
        {(hits.length > 0 || searching) && (
          <ul className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-white/10 bg-[#0b1a24] shadow-xl text-sm">
            {searching && <li className="px-3 py-2 text-[var(--muted)]">Szukam ulic i miejsc…</li>}
            {hits.map((h) => (
              <li key={`${h.lat}-${h.lng}-${h.name}`}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-white/5 flex gap-2 items-start"
                  onClick={() => {
                    setPin({ lat: h.lat, lng: h.lng })
                    setQuery(h.name)
                    setHits([])
                  }}
                >
                  <span className="shrink-0 mt-0.5 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/10 text-[var(--accent-2)]">
                    {h.kind || 'miejsce'}
                  </span>
                  <span className="leading-snug">{h.name}</span>
                </button>
              </li>
            ))}
            {!searching && hits.length === 0 && query.trim().length >= 2 && (
              <li className="px-3 py-2 text-[var(--muted)]">Brak wyników — spróbuj inną ulicę.</li>
            )}
          </ul>
        )}
      </div>

      <div
        className={`rounded-xl px-3 py-2 text-sm border ${
          illuminated === null
            ? 'border-white/10'
            : illuminated
              ? 'border-[var(--ok)]/40 bg-[var(--ok)]/10'
              : 'border-[var(--danger)]/40 bg-[var(--danger)]/10'
        }`}
      >
        {buildingsLoading ? (
          <span>Ładuję geometrię budynków…</span>
        ) : illuminated === null ? (
          <span>Kliknij mapę lub wyszukaj ulicę, aby sprawdzić nasłonecznienie.</span>
        ) : illuminated ? (
          <span>
            <strong className="text-[var(--ok)]">Oświetlone</strong> — słońce dochodzi do pineski o{' '}
            {formatTime(dateTime)}.
          </span>
        ) : sun.altitudeDeg <= 0 ? (
          <span>
            <strong className="text-[var(--muted)]">Noc</strong> — słońce pod horyzontem.
          </span>
        ) : (
          <span>
            <strong className="text-[var(--danger)]">W cieniu</strong> — zabudowa zasłania to miejsce o{' '}
            {formatTime(dateTime)}.
          </span>
        )}
      </div>

      <p className="text-xs text-[var(--muted)] tabular-nums">
        {lat.toFixed(5)}, {lng.toFixed(5)} · golden {formatTime(times.goldenHour)}–
        {formatTime(times.sunset)}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {(
          [
            ['standard', 'OSM'],
            ['lineart', 'Line art'],
            ['satellite', 'Satelita'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMapStyle(id)}
            className={`text-xs px-2.5 py-1 rounded-md border ${
              mapStyle === id
                ? 'border-[var(--accent)] bg-[var(--accent)]/15'
                : 'border-white/10 hover:bg-white/5'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPitchEnabled(!pitchEnabled)}
          className="text-xs px-2.5 py-1 rounded-md border border-white/10 hover:bg-white/5"
        >
          {pitchEnabled ? '3D + relief' : '2D'}
        </button>
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Wektor kadru</p>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded-md border border-white/10"
            onClick={() =>
              setFrame(
                frame?.active
                  ? null
                  : { bearing: Math.round(sun.azimuthDeg), active: true },
              )
            }
          >
            {frame?.active ? 'Wyłącz' : 'Włącz'}
          </button>
        </div>
        {frame?.active && (
          <>
            <label className="block text-xs text-[var(--muted)]">
              Kierunek kamery: {Math.round(frame.bearing)}°
              <input
                className="slider w-full mt-1"
                type="range"
                min={0}
                max={359}
                value={frame.bearing}
                onChange={(e) =>
                  setFrame({ bearing: Number(e.target.value), active: true })
                }
              />
            </label>
            {relation && (
              <p className="text-sm text-[var(--accent-2)]">{lightRelationLabel(relation)}</p>
            )}
          </>
        )}
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">Moje spoty ({savedSpots.length}/3)</p>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded-md bg-white/10 hover:bg-white/15"
            onClick={() => {
              if (!pin) return
              const name = query.trim() || `Spot ${pin.lat.toFixed(3)}, ${pin.lng.toFixed(3)}`
              const ok = addSavedSpot({ name, lat: pin.lat, lng: pin.lng })
              setMsg(ok ? 'Zapisano.' : 'Limit 3 lokalizacji (free).')
              window.setTimeout(() => setMsg(null), 2500)
            }}
          >
            Zapisz
          </button>
        </div>
        {msg && <p className="text-xs text-[var(--accent)]">{msg}</p>}
        <ul className="space-y-1 max-h-28 overflow-auto text-sm">
          {savedSpots.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2">
              <button
                type="button"
                className="text-left hover:text-[var(--accent)] truncate"
                onClick={() => setPin({ lat: s.lat, lng: s.lng })}
              >
                {s.name}
              </button>
              <button
                type="button"
                className="text-[var(--muted)] hover:text-[var(--danger)] text-xs"
                onClick={() => removeSavedSpot(s.id)}
              >
                usuń
              </button>
            </li>
          ))}
          {savedSpots.length === 0 && (
            <li className="text-xs text-[var(--muted)]">Brak zapisanych lokalizacji.</li>
          )}
        </ul>
      </div>
    </section>
  )
}
